import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import {
  Button, Card, Counters, SearchInput, Select, usePageTitle,
} from '../../components/app2';
import { authService } from '../../utils/authService';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import AdminUsersList from './components/AdminUsersList';
import CreateAdminModal from './components/CreateAdminModal';
import AdminActionsToolbar from './components/AdminActionsToolbar';

const CompanyAdministratorManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // La empresa llega por navigation state (flujo normal desde Gestión de Empresas)
  // o, como fallback, por ?companyId=N en la URL (acceso directo / recarga). En el
  // segundo caso se reconstruye desde el perfil del usuario (getUserProfile trae
  // sus companies con admins), evitando el rebote a company-management.
  const companyData = location?.state || {};
  const companyIdFromQuery = searchParams?.get('companyId');

  // Estado de la empresa resuelta (state → o fetch por companyId).
  const [company, setCompany] = useState({
    id: companyData?.companyId || (companyIdFromQuery ? Number(companyIdFromQuery) : null),
    name: companyData?.companyName || null,
    rfc: companyData?.companyRfc || null
  });
  const companyId = company?.id;
  const companyName = company?.name;
  const companyRfc = company?.rfc;

  // State management
  const [admins, setAdmins] = useState(companyData?.admins || []);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);

  // El header del shell nombra la empresa cuyos administradores se ven.
  usePageTitle({ kicker: 'Plataforma', title: companyName ? `Administradores · ${companyName}` : null });

  // Filter and search options
  const statusOptions = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Activos' },
    { value: 'inactive', label: 'Inactivos' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Nombre A-Z' },
    { value: 'name_desc', label: 'Nombre Z-A' },
    { value: 'email', label: 'Correo A-Z' },
    { value: 'created', label: 'Más recientes' },
    { value: 'last_login', label: 'Último acceso' }
  ];

  // Initialize component with company validation.
  // Si ya tenemos nombre (vino por state) no hay nada que resolver. Si solo
  // tenemos el id (acceso directo por URL), se busca la empresa en el perfil.
  // Solo se redirige a company-management cuando NO hay ninguna forma de saber
  // qué empresa es. `navigate` se excluye de deps (inestable en RR 6.0.2 → loop).
  useEffect(() => {
    if (companyName) {
      return; // datos completos (flujo normal desde Gestión de Empresas)
    }

    const idToResolve = companyData?.companyId || (companyIdFromQuery ? Number(companyIdFromQuery) : null);
    if (!idToResolve) {
      console.error('❌ Sin companyId (ni state ni URL), redirigiendo a Gestión de Empresas');
      navigate('/app/company-management');
      return;
    }

    // Reconstruir la empresa + sus admins desde el perfil del usuario.
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const profile = await authService?.getUserProfile();
        const found = profile?.companies?.find((c) => Number(c?.id) === Number(idToResolve));
        if (cancelled) return;
        if (!found) {
          console.error(`❌ Empresa ${idToResolve} no encontrada en el perfil, redirigiendo`);
          navigate('/app/company-management');
          return;
        }
        setCompany({ id: found.id, name: found.name, rfc: found.rfc });
        setAdmins(found.admins || []);
      } catch (e) {
        if (!cancelled) {
          console.error('❌ Error resolviendo empresa por URL:', e);
          navigate('/app/company-management');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyName]);

  // Filter and sort admins
  const filteredAndSortedAdmins = admins?.filter(admin => {
    const matchesSearch = 
      admin?.first_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      admin?.last_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      admin?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && admin?.is_active) ||
      (statusFilter === 'inactive' && !admin?.is_active);
      
    return matchesSearch && matchesStatus;
  })?.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return `${a?.first_name} ${a?.last_name}`?.localeCompare(`${b?.first_name} ${b?.last_name}`);
      case 'name_desc':
        return `${b?.first_name} ${b?.last_name}`?.localeCompare(`${a?.first_name} ${a?.last_name}`);
      case 'email':
        return a?.email?.localeCompare(b?.email);
      case 'created':
        return new Date(b?.createdAt) - new Date(a?.createdAt);
      case 'last_login':
        const aLogin = a?.last_login ? new Date(a?.last_login) : new Date(0);
        const bLogin = b?.last_login ? new Date(b?.last_login) : new Date(0);
        return bLogin - aLogin;
      default:
        return 0;
    }
  });

  // UPDATED: Handle password reset with SweetAlert2
  const handleResetPassword = async (userId) => {
    const confirmed = await AlertService?.confirm(
      'Restablecer contraseña',
      '¿Está seguro de que desea restablecer la contraseña de este administrador? Se enviará un correo con las nuevas credenciales.',
      {
        confirmText: 'Sí, restablecer',
        cancelText: 'Cancelar',
        confirmColor: '#f59e0b'
      }
    );

    if (!confirmed) return;

    try {
      console.log('🔄 Resetting password for user:', userId);

      // Show loading
      AlertService?.loading('Restableciendo contraseña...', 'Por favor espere mientras se procesa la solicitud.');

      const response = await strapiService?.post('users/reset-password', {
        userId: userId
      });

      // Close loading
      AlertService?.close();

      if (response?.status === 200) {
        await AlertService?.success(
          'Contraseña restablecida',
          'Se envió un email al usuario con sus nuevas credenciales.'
        );
      }

      console.log('✅ Password reset successful:', response?.message);
    } catch (error) {
      console.error('❌ Error resetting password:', error);
      
      // Close loading
      AlertService?.close();
      
      if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          'No tienes permisos para resetear la contraseña de este usuario'
        );
      } else if (error?.response?.status === 404) {
        await AlertService?.error(
          'Usuario no encontrado',
          'El usuario especificado no existe en el sistema'
        );
      } else {
        await AlertService?.error(
          'Error al restablecer contraseña',
          'Ocurrió un error inesperado. Por favor, intente nuevamente.'
        );
      }
    }
  };

  // UPDATED: Handle admin creation using SweetAlert2
  const handleCreateAdmin = async (adminData) => {
    setIsCreatingAdmin(true);
    
    try {
      console.log('🔄 Creating new administrator with dedicated API method...', {
        companyId,
        adminData: { ...adminData, password: '[PROTECTED]' }
      });

      // Show loading
      AlertService?.loading('Creando administrador...', 'Por favor espere mientras se procesa la creación del usuario.');

      // Use the dedicated API method
      const response = await strapiService?.users?.createCompanyAdmin({
        email: adminData?.email,
        password: adminData?.password,
        companyId: companyId,
        firstName: adminData?.firstName,
        lastName: adminData?.lastName
      });

      console.log('✅ Administrator created successfully:', response?.message || 'Success');
      
      // Extract user data from the documented response structure  
      const createdUser = response?.data || {};
      
      // Add the new admin to the list using actual response data
      const newAdmin = {
        id: createdUser?.id,
        documentId: createdUser?.documentId,
        email: createdUser?.email || adminData?.email,
        first_name: createdUser?.first_name || adminData?.firstName,
        last_name: createdUser?.last_name || adminData?.lastName,
        user_type: createdUser?.user_type || 'company_admin',
        is_active: createdUser?.is_active ?? true,
        must_change_password: createdUser?.must_change_password ?? true,
        last_login: createdUser?.last_login || null,
        createdAt: createdUser?.createdAt || new Date()?.toISOString(),
        username: createdUser?.username,
        provider: createdUser?.provider || 'local',
        confirmed: createdUser?.confirmed ?? true,
        blocked: createdUser?.blocked ?? false,
        company: createdUser?.company || companyId,
        role: createdUser?.role
      };

      setAdmins(prevAdmins => [...prevAdmins, newAdmin]);
      setShowCreateModal(false);

      // Close loading
      AlertService?.close();

      // Success message based on documented response
      const successMessage = response?.message || 'Administrador creado exitosamente';
      const adminName = `${newAdmin?.first_name} ${newAdmin?.last_name}`;
      
      await AlertService?.success(
        'Administrador creado exitosamente',
        `${adminName} ha sido agregado como administrador de ${companyName}. Se ha enviado un email de bienvenida con las credenciales.`,
        { autoClose: true }
      );
      
      console.log('✅ Admin list updated, modal closed, user notified');
      
      return { success: true, admin: newAdmin };
      
    } catch (error) {
      console.error('❌ Error creating administrator:', error);
      
      // Close loading
      AlertService?.close();
      
      // Handle specific error status codes as documented
      let errorTitle = 'Error al crear administrador';
      let errorMessage = 'Ocurrió un error inesperado. Por favor, intente nuevamente.';
      
      if (error?.response?.status === 400) {
        errorTitle = 'Datos incorrectos';
        errorMessage = 'Error en los datos proporcionados o el usuario ya existe';
      } else if (error?.response?.status === 403) {
        errorTitle = 'Sin permisos';
        errorMessage = 'No tienes permisos para crear administradores de empresa';
      } else if (error?.response?.status === 404) {
        errorTitle = 'Empresa no encontrada';
        errorMessage = 'La empresa especificada no fue encontrada en el sistema';
      } else if (error?.response?.data?.message) {
        errorMessage = error?.response?.data?.message;
      }
      
      await AlertService?.error(errorTitle, errorMessage);
      
      return { success: false, error: errorMessage };
      
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  // UPDATED: Handle admin deletion with SweetAlert2 and API integration
  const handleDeleteAdmin = async (adminId) => {
    const adminToDelete = admins?.find(admin => admin?.id === adminId);
    const adminName = adminToDelete 
      ? `${adminToDelete?.first_name} ${adminToDelete?.last_name}` 
      : 'este administrador';

    const confirmed = await AlertService?.confirmDelete(
      adminName,
      'Se eliminarán todos los permisos y accesos asociados a este usuario.'
    );

    if (!confirmed) return;

    try {
      console.log('🗑️ Deleting administrator:', { adminId, companyId });

      AlertService?.loading('Eliminando administrador...', 'Por favor espere mientras se procesa la eliminación.');

      // Use the dedicated API method according to documentation
      const response = await strapiService?.users?.deleteCompanyAdmin(companyId, adminId);

      console.log('✅ Administrator deleted successfully:', response?.message || 'Success');

      // Remove from local state
      setAdmins(prevAdmins => prevAdmins?.filter(admin => admin?.id !== adminId));
      setSelectedAdmins(prevSelected => prevSelected?.filter(id => id !== adminId));

      AlertService?.close();
      
      // Show success message based on API response
      const successMessage = response?.message || 'Administrador eliminado exitosamente';
      await AlertService?.successToast(successMessage);

      console.log('✅ Administrator deleted and UI updated successfully');
    } catch (error) {
      console.error('❌ Error deleting administrator:', error);
      AlertService?.close();
      
      // Handle specific error status codes as documented
      if (error?.response?.status === 400) {
        await AlertService?.error(
          'Error en la solicitud',
          'Error: el usuario no es válido o no pertenece a esta empresa'
        );
      } else if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          'Solo Super Admin puede eliminar Company Admins'
        );
      } else if (error?.response?.status === 404) {
        await AlertService?.error(
          'Usuario no encontrado',
          'El usuario especificado no existe en el sistema'
        );
      } else {
        await AlertService?.error(
          'Error al eliminar administrador',
          'No se pudo completar la eliminación. Por favor, intente nuevamente.'
        );
      }
    }
  };

  // UPDATED: Handle bulk operations with SweetAlert2
  const handleBulkDelete = async () => {
    if (selectedAdmins?.length === 0) return;

    const confirmed = await AlertService?.confirmDelete(
      `${selectedAdmins?.length} administrador${selectedAdmins?.length > 1 ? 'es' : ''}`,
      'Se eliminarán todos los permisos y accesos asociados a estos usuarios.'
    );

    if (!confirmed) return;

    try {
      console.log('🗑️ Bulk deleting administrators:', selectedAdmins);

      AlertService?.loading(
        'Eliminando administradores...',
        `Procesando eliminación de ${selectedAdmins?.length} usuario${selectedAdmins?.length > 1 ? 's' : ''}.`
      );

      // Delete each admin individually using the backend API
      const deletePromises = selectedAdmins.map(adminId =>
        strapiService?.users?.deleteCompanyAdmin(companyId, adminId)
      );

      // Wait for all deletions to complete
      const results = await Promise.allSettled(deletePromises);

      // Count successful deletions
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failedCount = results.filter(result => result.status === 'rejected').length;

      console.log(`✅ Bulk delete results: ${successCount} successful, ${failedCount} failed`);

      // Remove successfully deleted admins from local state
      if (successCount > 0) {
        setAdmins(prevAdmins =>
          prevAdmins?.filter(admin => !selectedAdmins?.includes(admin?.id))
        );
        setSelectedAdmins([]);
      }

      AlertService?.close();

      if (failedCount === 0) {
        await AlertService?.successToast(
          `${successCount} administrador${successCount > 1 ? 'es eliminados' : ' eliminado'} exitosamente`
        );
      } else if (successCount > 0) {
        await AlertService?.warning(
          'Eliminación parcial',
          `Se eliminaron ${successCount} de ${selectedAdmins?.length} administradores. ${failedCount} fallaron.`
        );
      } else {
        throw new Error('All deletions failed');
      }

      console.log('✅ Bulk delete completed');
    } catch (error) {
      console.error('❌ Error in bulk delete:', error);
      AlertService?.close();
      await AlertService?.error(
        'Error en eliminación masiva',
        'No se pudieron eliminar los administradores. Por favor, intente nuevamente.'
      );
    }
  };

  // Handle selection
  const handleSelectAdmin = (adminId) => {
    setSelectedAdmins(prev => 
      prev?.includes(adminId) 
        ? prev?.filter(id => id !== adminId)
        : [...prev, adminId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAdmins?.length === filteredAndSortedAdmins?.length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(filteredAndSortedAdmins?.map(admin => admin?.id));
    }
  };

  // Navigation handlers
  const handleBackToCompanies = () => {
    navigate('/app/company-management');
  };

  const isAllSelected = filteredAndSortedAdmins?.length > 0 &&
    selectedAdmins?.length === filteredAndSortedAdmins?.length;

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>{`Administradores de ${companyName || 'Empresa'} - PaySlip`}</title>
        <meta
          name="description"
          content={`Gestione los administradores de ${companyName || 'la empresa'} con capacidades completas de administración de usuarios`}
        />
      </Helmet>

      {/* El nombre de la empresa lo pinta el header del shell (usePageTitle),
          así que aquí no se repite como <h1>. */}
      <Button
        variant="ghost"
        size="sm"
        icon="arrow_back"
        onClick={handleBackToCompanies}
        style={{ paddingLeft: 0, marginBottom: 12 }}
      >
        Volver a empresas
      </Button>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 16,
        }}
      >
        <p className="a2-t-muted" style={{ font: "400 .95rem/1.5 'Hanken Grotesk'", margin: 0, minWidth: 0 }}>
          Gestiona quién puede operar esta empresa.
          {companyRfc ? <> RFC <span className="a2-mono">{companyRfc}</span>.</> : null}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginLeft: 'auto' }}>
          <Counters
            items={[
              { label: 'Administradores', value: admins?.length ?? 0 },
              { label: 'Activos', value: admins?.filter((a) => a?.is_active)?.length ?? 0, tone: 'ok' },
              { label: 'Mostrados', value: filteredAndSortedAdmins?.length ?? 0 },
            ]}
          />
          <Button icon="person_add" onClick={() => setShowCreateModal(true)}>
            Agregar Administrador
          </Button>
        </div>
      </div>

      <Card pad={false}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ marginBottom: 14, minWidth: 0 }}>
            <h3 className="a2-card-title">Administradores de la empresa</h3>
            <p className="a2-card-sub">
              Pueden ver a los colaboradores, sincronizar con el SAT y gestionar el plan
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              placeholder="Buscar por nombre o correo…"
              aria-label="Buscar administrador"
              style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 9, background: 'var(--surface-2)' }}
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              aria-label="Filtrar por estado"
              style={{ width: 'auto', minWidth: 165, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              aria-label="Ordenar"
              style={{ width: 'auto', minWidth: 160, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </div>
        </div>

        {/* Barra de selección: solo aparece cuando hay algo seleccionado */}
        <AdminActionsToolbar
          selectedCount={selectedAdmins?.length}
          onBulkDelete={handleBulkDelete}
          onClearSelection={() => setSelectedAdmins([])}
        />

        <AdminUsersList
          admins={filteredAndSortedAdmins}
          selectedAdmins={selectedAdmins}
          isAllSelected={isAllSelected}
          onSelectAll={handleSelectAll}
          onSelectAdmin={handleSelectAdmin}
          onDeleteAdmin={handleDeleteAdmin}
          onResetPassword={handleResetPassword}
          loading={loading}
          emptyStateProps={{
            hasFilters: Boolean(searchTerm) || statusFilter !== 'all',
            companyName,
            onCreateNew: () => setShowCreateModal(true)
          }}
        />
      </Card>

      {/* Create Administrator Modal */}
      {showCreateModal && (
        <CreateAdminModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateAdmin}
          isLoading={isCreatingAdmin}
          companyName={companyName}
        />
      )}
    </div>
  );
};

export default CompanyAdministratorManagement;